import { CardElement, ColorRank, PlayStyle } from "../types/battle"
import { isMaskedViewNativeAvailable } from "../utils/maskedViewSupport"

export const ELEMENT_NAMES: Record<CardElement, string> = {
  FIRE: "ОГОНЬ",
  WATER: "ВОДА",
  EARTH: "ЗЕМЛЯ",
  LIGHTNING: "МОЛНИЯ",
  WIND: "ВЕТЕР",
  ICE: "ЛЁД",
}

export const RANK_NAMES: Record<ColorRank, string> = {
  WHITE: "БЕЛЫЙ",
  GREEN: "ЗЕЛЁНЫЙ",
  BLUE: "СИНИЙ",
  PURPLE: "ФИОЛЕТОВЫЙ",
  ORANGE: "ОРАНЖЕВЫЙ",
  RED: "КРАСНЫЙ",
}

export const STYLE_ICONS: Record<PlayStyle, string> = {
  ATTACK: "⚔️",
  DEFENSE: "🛡️",
  SPEED: "💨",
}

export const ELEMENT_ICONS: Record<CardElement, string> = {
  FIRE: "🔥",
  WATER: "💧",
  EARTH: "🪨",
  LIGHTNING: "⚡",
  WIND: "🌪️",
  ICE: "❄️",
}

export const ELEMENT_COLORS: Record<CardElement, string> = {
  FIRE: "#C2410C",
  WATER: "#0369A1",
  EARTH: "#57534E",
  LIGHTNING: "#FFDE21",
  WIND: "#0891B2",
  ICE: "#0284C7",
}

/** Основной цвет ранга — рамка карты, акценты, градиент (light → main) */
export const RANK_COLORS: Record<ColorRank, string> = {
  WHITE: "#F7F7F7",
  GREEN: "#DCFCE7",
  BLUE: "#DBEAFE",
  PURPLE: "#F3E8FF",
  ORANGE: "#FFEDD5",
  RED: "#FEE2E2",
}

/** Тёмный оттенок — обводка карты, текст/иконка на бейдже (фон light) */
export const RANK_BORDER_COLORS: Record<ColorRank, string> = {
  WHITE: "#B0B0B0",
  GREEN: "#15803D",
  BLUE: "#1D4ED8",
  PURPLE: "#7E22CE",
  ORANGE: "#C2410C",
  RED: "#B91C1C",
}

/** Текст ранга на светлом фоне (бейдж) */
export const RANK_TEXT_ON_LIGHT: Record<ColorRank, string> = {
  WHITE: "#374151",
  GREEN: "#14532D",
  BLUE: "#1E3A8A",
  PURPLE: "#581C87",
  ORANGE: "#7C2D12",
  RED: "#7F1D1D",
}

/** Светлый фон карты (Z-полоса, отступы), бейдж ранга */
export const CARD_RANK_SURFACE: Record<ColorRank, string> = {
  WHITE: "#E8E8E8",
  GREEN: "#22C55E",
  BLUE: "#3B82F6",
  PURPLE: "#A855F7",
  ORANGE: "#F97316",
  RED: "#EF4444",
}

export function rankBorderColor(rank: ColorRank): string {
  return RANK_BORDER_COLORS[rank] ?? RANK_COLORS.WHITE
}

const GRADIENT_RANKS: ColorRank[] = ["GREEN", "BLUE", "PURPLE", "ORANGE", "RED"]

/** С GREEN и выше — градиент RANK_COLORS (light) → CARD_RANK_SURFACE (main) */
export function usesRankGradient(rank: ColorRank): boolean {
  return GRADIENT_RANKS.includes(rank)
}

export function getRankGradientColors(
  rank: ColorRank
): readonly [string, string] {
  return [RANK_COLORS[rank], CARD_RANK_SURFACE[rank]]
}

export function getRankSurfaceColor(rank: ColorRank): string {
  return CARD_RANK_SURFACE[rank]
}

/** Цвет свечения за картой (на WHITE — серый, иначе main ранга) */
export function getRankGlowColor(rank: ColorRank): string {
  if (rank === "WHITE") return "#9CA3AF"
  return CARD_RANK_SURFACE[rank]
}

export const CARD_SIZES = {
  sm: 115,
  md: 140,
  lg: 200,
  arena: 160,
} as const

/** Dev toggle: mock duel без backend */
export const USE_MOCK_BATTLE = false//__DEV__ && true

/** Дуэль колодой (6 карт). */
export const DECK_BATTLE_ENABLED = true

/** Показать «Хранители» в меню → Сервисы. Включить, когда стабилизируем релиз. */
export const GUARDIANS_MENU_VISIBLE = true

/**
 * Коллекция / профиль: принудительный lite (без MaskedView).
 * false — фигурная маска, если RNCMaskedView есть в нативной сборке.
 */
export const GUARDIAN_CARD_LITE_RENDER = false

/** Учитывает OTA-сборки без нативного MaskedView. */
export function resolveGuardianCardLiteRender(
  preferLite = GUARDIAN_CARD_LITE_RENDER
): boolean {
  if (preferLite) return true
  return !isMaskedViewNativeAvailable()
}
